import { useState, useMemo, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Row, Col, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUsers } from '../utils/api';
import Header from './Header';
import Avatar from './Avatar';
import Loader from './Loader';
import { faPlus, faPencil, faUsers, faX } from '@fortawesome/free-solid-svg-icons';
import ShowMenuButton from './ShowMenuButton';
import CreateEditUserModal from './Modals/CreateEditUserModal';
import { tableSort } from '../utils/helpers';
import Error from './Error';
import ViewUserModal from './Modals/ViewUserModal';
import { useDataStore } from '../Store';

const Users = () => {

    const selectedEntity = useDataStore((state) => state.selectedEntity);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);

    const [selectedUser, setSelectedUser] = useState(null);
    const [showCreditEditModal, setShowCreditEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);

    const fetchData = async () => {
        const data = await getUsers(selectedEntity, page, 30, searchText);
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [searchText, page]);

    const createUser = () => {
        setSelectedUser(null)
        setShowCreditEditModal(true);
    };
    const rowClicked = (o) => {
        setSelectedUser(o);
        setShowViewModal(true);
    }
    const handleActionClick = (name, user) => {
        if (name === 'View') {
            rowClicked(user);
            return;
        }

        if (name === 'Edit') {
            setSelectedUser(user);
            setShowCreditEditModal(true);
        }
    }

    const lastPage = data.LastPage;

    return (
        <div style={{ padding: 10 }}>
            {loading ? <Loader /> : error ? <Error /> : (
                <>
                    <Header title='Users' description="Manage user identities, including creating and provisioning users, blocking access, and deleting accounts." buttons={[{ id: 1, label: 'Create User', variant: 'primary', icon: faPlus, onClick: () => createUser() }]} />
                    <Row className="mb-3">
                        <Col md={12}>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchText}
                                    onChange={(e) => {
                                        setPage(1);
                                        setSearchText(e.target.value)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            setPage(1);
                                            fetchData();
                                        }
                                    }}
                                />
                                <Button variant="outline-secondary" onClick={fetchData}>
                                    Search
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col>
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <Table striped bordered hover style={{ minHeight: 190, overflow: 'visible' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                ID
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                ExternalId
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                First Name
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Last Name
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Email
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Dealers
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Role
                                            </th>
                                             <th style={{ whiteSpace: 'nowrap' }}>
                                                Blocked
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.Results?.map((d, idx) => (
                                            <tr key={idx}>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.ExternalId}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.FirstName}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.LastName}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Email}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.UserRoles?.some(c => c.Role.Id === 3) ? 'All' : (new Set(d?.UserEntityDealers?.map(c=>c?.Dealer?.Id).filter(c=>c)).size)}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Role}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Blocked ? "Yes" : "No"}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}>{<ShowMenuButton menuItems={[
                                                    { icon: <FontAwesomeIcon icon={faUsers} />, name: 'View', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faPencil} />, name: 'Edit', target: d, onClick: handleActionClick }
                                                ]} />}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.First disabled={page <= 1} onClick={() => setPage(1)} />
                                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                                <Pagination.Item key={page} active={true}>
                                    {page}
                                </Pagination.Item>
                                <Pagination.Next disabled={page >= lastPage} onClick={() => setPage(page + 1)} />
                                <Pagination.Last disabled={page >= lastPage} onClick={() => setPage(lastPage)} />
                            </Pagination>
                        </Col>
                    </Row>

                    <ViewUserModal
                        show={showViewModal}
                        onHide={() => setShowViewModal(false)}
                        user={selectedUser}
                    />

                    <CreateEditUserModal
                        show={showCreditEditModal}
                        onHide={(shouldRefresh) => {
                            setShowCreditEditModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                        user={selectedUser}
                    />
                </>
            )}
        </div>
    );
};

export default Users;
